/* helper.ts */

/* Constants */
import { InputErrorMessage } from "src/shared/constants/common/ErrorMessage";

/* Interfaces */
import {
  CustomCohortsState,
  Status,
} from "src/shared/interfaces/custom-cohorts/CustomCohorts";

import {
  BasicProps,
  CustomCohortsProps,
  LogicalProps,
  ProcessedCohort,
} from "src/shared/interfaces/custom-cohorts/CustomCohorts";

const MIN_LENGTH: number = 3;

export const findCohortById = (
  diseaseId: string,
  state: CustomCohortsState,
  id: string
) => {
  return state.customCohortsByDisease[
    diseaseId
  ].cohorts.find(
    (cohort: CustomCohortsProps) => cohort.id === id
  );
};

export const findCohortIndexById = (
  state: CustomCohortsState,
  id: string
) => {
  const diseaseId = state.currentDiseaseId;
  if (
    !diseaseId ||
    !state.customCohortsByDisease[diseaseId]
  )
    return -1;

  return state.customCohortsByDisease[
    diseaseId
  ].cohorts.findIndex(
    (cohort: CustomCohortsProps) => cohort.id === id
  );
};

export const validateCohortName = (
  diseaseId: string,
  state: CustomCohortsState,
  name: string
) => {
  if (isDuplicated(diseaseId, state, name)) {
    throw new Error(InputErrorMessage.DUPLICATE_NAME);
  }
  checkNameLength(name);
};

const isDuplicated = (
  diseaseId: string,
  state: CustomCohortsState,
  name: string
) => {
  return state.customCohortsByDisease[
    diseaseId
  ]?.cohorts.some(
    (cohort: CustomCohortsProps) =>
      cohort.name.trim().toLowerCase() ===
      name.trim().toLowerCase()
  );
};

const checkNameLength = (name: string) => {
  if (name.length < MIN_LENGTH) {
    throw new Error(InputErrorMessage.MIN_LENGTH);
  }
  return true;
};

export const transformCohortListToString = (
  cohortList: CustomCohortsProps[]
): string | undefined => {
  if (!cohortList) {
    return undefined;
  }

  const processCohort = (
    cohort: BasicProps | LogicalProps
  ): ProcessedCohort => {
    if ("entity" in cohort) {
      return {
        entity_name: cohort.entity.name,
        date_restriction: cohort.dateRestriction,
        value: cohort.value,
        values: cohort.values,
        lower_bound: cohort.boundaries?.lowerBound,
        upper_bound: cohort.boundaries?.upperBound,
      };
    }
    return {
      operator: cohort.operator,
      conditions: cohort.conditions.map(processCohort),
    };
  };

  const transformedObject = cohortList
    .filter((cohort) => cohort.show)
    .reduce(
      (
        acc: Record<string, any>,
        cohort: CustomCohortsProps
      ) => {
        if (cohort.data) {
          acc[cohort.name] = processCohort(cohort.data);
        }
        return acc;
      },
      {}
    );

  const result =
    Object.keys(transformedObject).length > 0
      ? JSON.stringify(transformedObject)
      : "";

  return result || undefined;
};

/**
 * There are 3 different types of status:
 * 'SAVED', 'MODIFIED', and 'EDITING'.
 * 'SAVED' means the cohort is saved in storage but not modified.
 * If the cohoort is editing, status will be set to
 * 'EDITING' and after saving, it will be set to 'MODIFIED'.
 */
export const checkStatus = (
  status?: Status,
  modifiedAt?: string
) => {
  if (status !== undefined) {
    return status;
  }
  if (modifiedAt) {
    return Status.MODIFIED;
  }
  return Status.SAVED;
};
